import { connect } from 'react-redux';

import TableDetailsLayout from 'components/layouts/TableDetailsLayout';
import { TableRegistry } from 'components/Tables';
import { DetailsRegistry } from 'components/DetailsPanels';
import { IconRegistry } from 'components/icons';
import { ToolbarRegistry } from 'components/Toolbars';
import {
  getSubmittableFileDefinitions,
  getFields,
  getEntityDefinition,
  buildRelatedEntitiesQueries,
} from 'utils/dictionaryHelpers';
import { HEADER_HEIGHT, TOP_NAV_HEIGHT } from 'style/dimensions';

import {
  parsePaginationParams,
  parseQueryParams,
  shouldListRefresh,
} from 'utils/location';
import withRouting from 'utils/withRouting';

import { fetchEntities } from 'ducks/list';
import { fetchEntity } from 'ducks/details';
import { fetchLinks } from 'ducks/links';
import { fetchRelatedEntities } from 'ducks/relatedEntities';
import { setMessage } from 'ducks/message';
import {
  fetchQCNodeTypeDetail,
  handleQCDownload,
} from 'ducks/qcReports';
import { DynamicEntityLink } from 'components/Links';

class Entity extends React.Component {
  componentDidMount() {
    const {
      dispatch,
      entityDefinitions: { defs },
      router: { paramlessPath: path },
      router,
      router: {
        params: {
          entity,
          program,
          project,
        },
        location: {
          query: {
            did,
            fileState,
            quickSearch,
            withLinks,
            withLinksAny,
            withoutLinks,
            withoutPathTo,
            withPathTo,
            withPathToAny,
          },
        },
      },
    } = this.props;

    if (defs.length) {
      const def = getEntityDefinition({ defs, entity });

      if (!def) {
        history.replaceState(null, "", `${program}/${project}/${path}/case`);
        dispatch(setMessage('The entity you have entered does not exist'));
        return;
      }

      const fields = getFields({ def });

      this.getEntities({
        defs,
        dispatch,
        entity,
        fields,
        fileState,
        id: did,
        path,
        program,
        project,
        router,
        quickSearch,
        withLinks,
        withLinksAny,
        withoutLinks,
        withoutPathTo,
        withPathTo,
        withPathToAny,
      });

      if (did) {
        const { queries } = buildRelatedEntitiesQueries({
          defs, entityType: entity,
        });
        const submittedFileTypes = getSubmittableFileDefinitions({ defs }).map(d => d.id);
        const biospecimenFileTypes = defs.filter(d => d.category === 'biospecimen').map(d => d.id);

        dispatch(fetchEntity({
          entity,
          id: did,
          isQCReports: path === 'qc',
          fields,
        }));

        if (entity === 'case') {
          dispatch(fetchRelatedEntities({
            id: did,
            queries,
          }));
        } else {
          dispatch(fetchLinks({
            entity,
            id: did,
          }, submittedFileTypes, biospecimenFileTypes));
        }
      }
    }
  }

  componentDidUpdate = (prevProps) => {
    const {
      dispatch,
      entityDefinitions: { defs: nDefs },
      router: { paramlessPath : nPath },
      router: nRouter,
      router: {
        params: { entity: nEntity },
        location: { query: nQuery },
      },
    } = this.props;

    const {
      entityDefinitions: { defs: pDefs },
      router: { paramlessPath : pPath },
      router: {
        location: { query: pQuery },
        params: {
          entity: pEntity,
          program,
          project,
        },
      },
      details: { isDetailsFetching },
    } = prevProps;
    const { did: nDid } = nQuery;
    const { did: pDid } = pQuery;

    if (nDefs.length) {
      const def = getEntityDefinition({ defs: nDefs, entity: nEntity });

      if (!def) {
        history.replaceState(null, "", `${program}/${project}/browse/case`);
        dispatch(setMessage('The entity you have entered does not exist'));
        return;
      }

      const fields = getFields({ def });

      if ((pEntity !== nEntity
        || pPath !== nPath
        || pDefs.length !== nDefs.length
        || shouldListRefresh(pQuery, nQuery))
      ) {
        const {
          fileState,
          quickSearch,
          withLinks,
          withLinksAny,
          withoutLinks,
          withoutPathTo,
          withPathTo,
          withPathToAny,
        } = nQuery;

        this.getEntities({
          defs: nDefs,
          dispatch,
          entity: nEntity,
          fields,
          fileState,
          id: nQuery.id,
          path: nPath,
          program,
          project,
          router: nRouter,
          quickSearch,
          withLinks,
          withLinksAny,
          withoutLinks,
          withoutPathTo,
          withPathTo,
          withPathToAny,
        });
      }

      if (nDid && !isDetailsFetching && (
        (pDid !== nDid)
        || (pEntity !== nEntity)
      )) {
        const { queries } = buildRelatedEntitiesQueries({
          defs: nDefs,
          entityType: nEntity,
        });
        const submittedFileTypes = getSubmittableFileDefinitions({ defs: nDefs }).map(d => d.id);
        const biospecimenFileTypes = nDefs.filter(d => d.category === 'biospecimen').map(d => d.id);

        dispatch(fetchEntity({
          entity: nEntity,
          id: nDid,
          isQCReports: nPath === 'qc',
          fields,
        }));

        if (nEntity === 'case') {
          dispatch(fetchRelatedEntities({
            id: nDid,
            queries,
          }));
        } else {
          dispatch(fetchLinks({
            entity: nEntity,
            id: nDid,
          }, submittedFileTypes, biospecimenFileTypes));
        }
      }
    }
  }

  getEntities = ({
    count,
    defs,
    dispatch,
    entity,
    fields,
    fileState,
    format,
    id,
    path,
    program,
    project,
    router,
    quickSearch,
    withLinks,
    withLinksAny,
    withoutLinks,
    withoutPathTo,
    withPathTo,
    withPathToAny,
  }) => {
    const {
      first,
      offset,
      order,
      sort,
      sort_by,
    } = parsePaginationParams(router, { path });

    if (path === 'qc') {
      const { error_type, severity } = parseQueryParams(router);
      dispatch(fetchQCNodeTypeDetail({
        entity,
        params: Object.assign(
          {
            error_type,
            sort_by: decodeURI(sort_by) || `${sort}:${order}`,
            severity,
          },
          format
            ? {
              format,
              limit: count,
            }
            : {
              limit: first,
              offset,
            },
        ),
        program,
        project,
      })).then(({
        payload,
        type,
      }) => type === 'gdc/QC_FILE_DOWNLOAD_REQUEST'
        && handleQCDownload(dispatch, payload));
    } else {
      const submittedFileTypes = getSubmittableFileDefinitions({ defs }).map(d => d.id);
      const biospecimenFileTypes = defs.filter(d => d.category === 'biospecimen').map(d => d.id);

      dispatch(fetchEntities(
        {
          entity,
          fields,
          first,
          offset,
          sort,
          order,
          program,
          project,
          quickSearch,
          id,
          fileState,
          withLinks,
          withLinksAny,
          withoutLinks,
          withoutPathTo,
          withPathTo,
          withPathToAny,
        },
        submittedFileTypes,
        biospecimenFileTypes,
      ));
    }
  };

  qcDownload = (format) => {
    const {
      dispatch,
      list,
      nodeTypeData,
      router: { paramlessPath : path },
      router,
      router: {
        params: {
          entity,
          program,
          project,
        },
      },
    } = this.props;

    const data = path === "qc" ? nodeTypeData : list;
    const { data: { count } } = data;

    this.getEntities({
      count,
      dispatch,
      entity,
      format,
      path,
      program,
      project,
      router,
    });
  }

  render(): React.Element {
    const {
      details,
      entityDefinitions: { defs },
      list,
      nodeTypeData,
      notifications,
      router,
      router: { params: { entity } },
      router: { paramlessPath : path }
    } = this.props;
    const data = path === "qc" ? nodeTypeData : list;
    const { sort, order } = parsePaginationParams(router, { path });

    return (
      <TableDetailsLayout
        details={details}
        download={this.qcDownload}
        entity={entity}
        height={`calc(100vh - ${
          (1 + notifications.filter(n => !n.dismissed).length)
          * HEADER_HEIGHT + TOP_NAV_HEIGHT
        }rem)`}
        IconComponent={IconRegistry(entity, defs)}
        DetailsComponent={DetailsRegistry(entity, defs)}
        LinkComponent={DynamicEntityLink(entity, path)}
        list={data}
        order={order}
        path={path}
        router={router}
        sort={sort}
        TableComponent={TableRegistry(path, entity, defs)}
        ToolbarComponent={ToolbarRegistry(path, entity, defs)}
      />
    );
  }
}

export { Entity as UnconnectedEntity };

export default connect(({
  list,
  details,
  entityDefinitions,
  notifications,
  qcReports: { nodeTypeData },
}) => ({
  list,
  nodeTypeData,
  details,
  entityDefinitions,
  notifications,
}))(withRouting(Entity));
